/**
 * 音声プレビューの「サンプル条件」(試聴用の代入値) の純関数ロジック — Issue #439。
 *
 * ## なぜサンプル条件が必要か
 *
 * 遅延系コンテンツの SSML テンプレートは `{delay_minutes_5}` `{route_name}`
 * `{from_station}` `{direction_name}` といったプレースホルダを含む。これを未展開のまま
 * Polly に送ると「かっこ デレイ ミニッツ…」と読み上げられて実用にならないため、
 * プレビュー時には必ず何らかの値を代入する必要がある。
 *
 * ## 「1 クリックで試聴できる」ことを優先する
 *
 * ユーザーに毎回サンプル値を入力させるのは面倒なので、**編集中の行の設定値から
 * サンプルを自動生成**する ({@link deriveSampleDelaySeconds})。
 * 「5 分以上の行を試聴したら『およそ 5 分の遅れ』と鳴る」という期待どおりの挙動になる。
 *
 * ## 実際の展開はサーバ側が行う
 *
 * ここで作るのは `POST /audio-preview` の `sample_context` に載せる値だけ。
 * SSML の組み立ては `lambda/common/preview_render.py` が UpdateContents と同じ
 * `ssml_builder` を呼んで行うため、フロントとサーバで文面がズレることはない。
 */
import type {
  BroadcastContent,
  ConnectingDelayJudgementMode,
  ConnectingLineDelayByStationProperties,
  ConnectingLineDelayProperties,
  DelayBroadcastPattern,
} from '../../api/types';
import { requiresConnectingStationDirections } from '../../utils/broadcastContents';

import {
  MAX_INITIAL_SAMPLE_DELAY_SECONDS,
  PLACEHOLDER_SAMPLE_DELAY_SECONDS,
  PLACEHOLDER_SAMPLE_DEPARTURE_TIME,
} from './constants';
import {
  CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS,
  CONNECTING_STATION_INTRO_PLACEHOLDERS,
  CONNECTING_STATION_LINE_PLACEHOLDERS,
  CONNECTING_DEPARTURE_FORECAST_PLACEHOLDERS,
  CONNECTING_TRANSFER_AWARE_PLACEHOLDERS,
  CONNECTING_WHOLE_LINE_PLACEHOLDERS,
  resolveConnectingStationDirectionPlaceholders,
  SELF_TRAIN_DELAY_PLACEHOLDERS,
} from './placeholders';
import type {
  PlaceholderItem,
  PreviewSampleContext,
  PreviewSampleFieldVisibility,
  PreviewSampleLimits,
  PreviewSampleLineContext,
  PreviewSampleLineLimit,
} from './types';

/**
 * フラットキー (`routeName` / `routeId` / `directionName`) と `lines[]` を
 * 相互に埋め合わせ、どちらを見ても同じ値になるようにする — Issue #445。
 *
 * ## なぜ必要か
 *
 * 接続線・方面は複数設定され得るため、プレビュー条件も `lines[]` (接続線の配列 ×
 * 方面の配列) を持つ必要がある。一方で
 *
 *   - part1 (`connecting_line_delay`) / `self_train_delay` は単一値しか使わない
 *   - 旧クライアント / 既存テストはフラットキーだけを送る・参照する
 *
 * ため、**両方の表現を常に同期** させておくのが最も壊れにくい。
 * フロントとサーバで解釈がズレないよう、変換規則はこの 1 か所に閉じ込める。
 *
 * - `lines` が空 (かつフラットキーに線名 / 方面がある) → `lines[0]` へ写す
 * - `lines` がある → 先頭を代表値としてフラットキーへ写す (未設定のキーのみ)
 */
export function normalizeSampleContext(sample: PreviewSampleContext): PreviewSampleContext {
  const lines = (sample.lines ?? []).filter(
    (line): line is PreviewSampleLineContext => line !== null && line !== undefined,
  );
  if (lines.length === 0) {
    if (!sample.routeName && !sample.routeId && !sample.directionName) return sample;
    // 単一指定 → lines[] へ写す (サーバは lines[] を優先して読む)。
    return {
      ...sample,
      lines: [
        {
          routeId: sample.routeId,
          routeName: sample.routeName,
          directionNames: sample.directionName ? [sample.directionName] : [],
        },
      ],
    };
  }
  // 複数指定 → 先頭を代表値としてフラットキーへ写す (part1 / 旧サーバ互換)。
  const head = lines[0]!;
  return {
    ...sample,
    lines,
    routeId: sample.routeId ?? head.routeId,
    routeName: sample.routeName ?? head.routeName,
    directionName: sample.directionName ?? head.directionNames?.[0],
  };
}

/**
 * 遅延放送パターンの行から「その行が選ばれる代表的な遅延秒数」を導出する。
 *
 * `delay_minutes_from` (以上) をそのまま採用する。境界値そのものを使うことで
 * 「この行を試聴する = この行が選ばれる最小の遅延で鳴らす」という直感的な挙動になり、
 * サーバ側の `select_delay_pattern()` (`from <= 分 < to`) でも確実に同じ行が選ばれる。
 *
 * - `delay_minutes_from` が未設定 / 不正値の行は既定値 (5 分) にフォールバックする。
 * - `delay_minutes_from` が 0 の行は「0 分以上」= 遅延なしでも鳴る行なので、
 *   0 秒だと分数が読み上げられず確認しづらい。`delay_minutes_to` があれば
 *   その 1 分手前、無ければ既定値を使って「実際に鳴りそうな値」にする。
 */
export function deriveSampleDelaySeconds(pattern: DelayBroadcastPattern | undefined): number {
  if (!pattern) return PLACEHOLDER_SAMPLE_DELAY_SECONDS;
  const from = pattern.delay_minutes_from;
  if (typeof from !== 'number' || !Number.isFinite(from) || from < 0) {
    return PLACEHOLDER_SAMPLE_DELAY_SECONDS;
  }
  if (from > 0) return Math.floor(from) * 60;

  // from === 0 の行: 0 秒だと「0分遅れ」になって確認しづらいので実用的な値にずらす。
  const to = pattern.delay_minutes_to;
  if (typeof to === 'number' && Number.isFinite(to) && to > 1) {
    return Math.min(PLACEHOLDER_SAMPLE_DELAY_SECONDS, (Math.floor(to) - 1) * 60);
  }
  return PLACEHOLDER_SAMPLE_DELAY_SECONDS;
}

/**
 * 凡例定義 (`PlaceholderItem[]`) の `sample` からサンプル条件の初期値を組み立てる。
 *
 * トークン名 → `PreviewSampleContext` のキーへのマッピングを 1 か所に閉じ込め、
 * 「凡例に載っているトークンだけがプレビュー条件パネルに現れる」ようにする
 * (whole_line モードのように乗換駅・方面を使わない画面では、それらの入力欄が
 *  そもそも表示されない = ユーザーの混乱を防げる)。
 *
 * `overrides` を渡すと自動導出値より優先される (編集中の行の値を反映する用途)。
 */
export function buildSampleContext(
  items: PlaceholderItem[],
  overrides: Partial<PreviewSampleContext> = {},
): PreviewSampleContext {
  const context: PreviewSampleContext = {};
  for (const item of items) {
    if (item.sample === undefined) continue;
    switch (item.token) {
      case '{route_name}':
      case '{route}':
        context.routeName ??= item.sample;
        break;
      case '{route_id}':
        context.routeId ??= item.sample;
        break;
      case '{from_station}':
      case '{from_prefix}':
        context.fromStation ??= item.sample;
        break;
      case '{transfer_station}':
        context.transferStation ??= item.sample;
        break;
      case '{direction_name}':
        context.directionName ??= item.sample;
        break;
      case '{departure_time}':
        // 凡例のサンプルは表示用 (`12時13分`)。サーバへ送るのは 24 時間表記の HH:MM。
        context.departureTime ??= PLACEHOLDER_SAMPLE_DEPARTURE_TIME;
        break;
      case '{headsign}':
        // 凡例のサンプルは「八王子行」。末尾の「行」はサーバが補うため外して持つ。
        context.headsign ??= item.sample.replace(/行き?$/, '');
        break;
      default:
        // 遅延分数系トークン ({delay_minutes_*} 等) は個別に持たず、
        // 唯一の正となる delaySeconds から**サーバ側が**全て導出する
        // (フロントで別々に持つと {delay_minutes_1} と {delay_minutes_5} が
        //  矛盾した値になり得るため)。
        break;
    }
  }
  if (context.delaySeconds === undefined) {
    context.delaySeconds = PLACEHOLDER_SAMPLE_DELAY_SECONDS;
  }
  // フラットキーと lines[] のどちらから見ても同じ条件になるよう正規化する (Issue #445)。
  return normalizeSampleContext({ ...context, ...stripUndefined(overrides) });
}

/** `undefined` の値を持つキーを除去する (overrides で既定値を消さないため) */
function stripUndefined(value: Partial<PreviewSampleContext>): Partial<PreviewSampleContext> {
  const result: Partial<PreviewSampleContext> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (entry !== undefined) {
      (result as Record<string, unknown>)[key] = entry;
    }
  }
  return result;
}

/**
 * 展開後の文面に残った `{...}` トークンを重複なく (出現順で) 返す。
 *
 * サーバも同等の判定を行う (`preview_render.find_unresolved_tokens`) が、
 * クライアント側でも保持しておくことで、サーバが `unresolved_tokens` を返さない
 * 旧レスポンスや、`rendered_ssml` を手元で再検査したいケースに対応できる。
 */
export function findUnresolvedTokens(text: string): string[] {
  if (typeof text !== 'string' || text === '') return [];
  const matches = text.match(/\{[^{}]*\}/g);
  if (!matches) return [];
  return Array.from(new Set(matches));
}

/**
 * サンプル条件パネルに表示する入力欄の種類を、凡例定義から判定する。
 *
 * 凡例に載っていない項目は「そのテンプレートでは使えないプレースホルダ」なので、
 * 入力欄自体を出さない (例: whole_line モードでは乗換駅・方面を出さない)。
 */
export function resolveSampleFields(items: PlaceholderItem[]): PreviewSampleFieldVisibility {
  const tokens = new Set(items.map((item) => item.token));
  return {
    /*
     * 遅延欄は分数系・秒数系のいずれかがあれば出す。
     *
     * ⚠️ `{delay_seconds}` / `{delay}` は SBI-2 で **凡例から撤去済み** だが、
     * 判定はここに残す。本関数は任意の凡例配列を受け取る汎用関数なので、
     * 「秒トークンだけを含む配列を渡したときに遅延欄が消える」という
     * 退行余地を作らないため (秒テンプレートはサーバ側では今も展開される)。
     */
    showDelay:
      tokens.has('{delay_minutes_1}') ||
      tokens.has('{delay_minutes_5}') ||
      tokens.has('{delay_minutes_10}') ||
      tokens.has('{delay_minutes}') ||
      tokens.has('{delay_seconds}') ||
      tokens.has('{delay}') ||
      tokens.has('{directions_text}'),

    showRouteName: tokens.has('{route_name}') || tokens.has('{route}'),
    showFromStation: tokens.has('{from_station}') || tokens.has('{from_prefix}'),
    showTransferStation: tokens.has('{transfer_station}'),
    showDirectionName: tokens.has('{direction_name}'),
    // 発車見込み案内の凡例だけが持つトークン (他のモードには出ない)。
    showDepartureTime: tokens.has('{departure_time}'),
    showHeadsign: tokens.has('{headsign}'),
  };
}

/**
 * 複数コンテンツぶんの入力欄可否を OR で畳み込む。
 *
 * 放送地点まるごとプレビュー (通し聴き) は「開始チャイム → contents[] → 終了チャイム」を
 * 一度に鳴らすため、**どれか 1 つのコンテンツで使われている条件は指定できる** 必要がある
 * (逆に、どのコンテンツでも使っていない条件は出さない = 個別プレビューと同じ方針)。
 */
export function mergeSampleFields(
  list: PreviewSampleFieldVisibility[],
): PreviewSampleFieldVisibility {
  return list.reduce<PreviewSampleFieldVisibility>(
    (acc, fields) => ({
      showDelay: acc.showDelay || fields.showDelay,
      showRouteName: acc.showRouteName || fields.showRouteName,
      showFromStation: acc.showFromStation || fields.showFromStation,
      showTransferStation: acc.showTransferStation || fields.showTransferStation,
      showDirectionName: acc.showDirectionName || fields.showDirectionName,
      showDepartureTime: acc.showDepartureTime || fields.showDepartureTime,
      showHeadsign: acc.showHeadsign || fields.showHeadsign,
    }),
    {
      showDelay: false,
      showRouteName: false,
      showFromStation: false,
      showTransferStation: false,
      showDirectionName: false,
      showDepartureTime: false,
      showHeadsign: false,
    },
  );
}

/**
 * 放送コンテンツ 1 件で使えるプレースホルダ凡例を、カテゴリ / 判定モードから解決する。
 *
 * 個別フィールドのプレビュー (`AudioPreviewField`) はフィールドごとに凡例配列を
 * 直接渡しているが、放送地点まるごとプレビューでは「コンテンツ全体で何が使えるか」を
 * 知る必要があるため、ここでカテゴリ → 凡例のマッピングを一元化する。
 *
 * - `free_text`: プレースホルダを展開しないため空配列 (条件パネルに何も出ない)。
 * - `connecting_line_delay`: 判定モードごとに使えるトークンが異なる
 *   (whole_line では乗換駅・方面が本番でも読み上げられない)。
 * - `connecting_line_delay_by_station`: 冒頭文 + ブロック文 + 方面部分文の
 *   三層すべてがサーバ側で組み立てられるため、3 つの凡例を結合する。
 */
export function resolveContentPlaceholders(content: BroadcastContent): PlaceholderItem[] {
  switch (content.category) {
    case 'self_train_delay':
      return SELF_TRAIN_DELAY_PLACEHOLDERS;
    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      const mode: ConnectingDelayJudgementMode = props.delay_judgement_mode ?? 'whole_line';
      if (mode === 'transfer_aware') return CONNECTING_TRANSFER_AWARE_PLACEHOLDERS;
      if (mode === 'departure_forecast') return CONNECTING_DEPARTURE_FORECAST_PLACEHOLDERS;
      if (mode === 'section_scheduled') return CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS;
      return CONNECTING_WHOLE_LINE_PLACEHOLDERS;
    }
    case 'connecting_line_delay_by_station': {
      /*
       * 方面部分文の凡例は **接続線ブロックごとの判定モード** で変わる。
       * `whole_line` (接続線全体の遅延) の接続線は方面を扱わないため
       * `{direction_name}` を含まない凡例になる。
       *
       * コンテンツ全体 (通し試聴) の凡例としては、**設定されている接続線のうち
       * 1 つでも方面を使うモードがあれば方面入りの凡例を採用** する
       * (使う接続線があるなら条件パネルで方面名を指定できる必要がある)。
       * 全接続線が whole_line なら方面欄は出さない。
       */
      const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
      const lines = Array.isArray(props.lines) ? props.lines : [];
      const usesDirections =
        lines.length === 0 ||
        lines.some((line) => requiresConnectingStationDirections(line.delay_judgement_mode));
      // 発車見込み案内 (SBI-6) の接続線が 1 本でもあれば、時刻 / 行先の入力欄を出すため
      // 発車見込み用の凡例 (`{departure_time}` / `{headsign}`) を足す。
      const usesDepartureForecast = lines.some(
        (line) => line.delay_judgement_mode === 'departure_forecast',
      );
      return [
        ...CONNECTING_STATION_INTRO_PLACEHOLDERS,
        ...CONNECTING_STATION_LINE_PLACEHOLDERS,
        ...resolveConnectingStationDirectionPlaceholders(
          usesDirections ? 'section_scheduled' : 'whole_line',
        ),
        ...(usesDepartureForecast
          ? resolveConnectingStationDirectionPlaceholders('departure_forecast').filter(
              (item) => item.token === '{departure_time}' || item.token === '{headsign}',
            )
          : []),
      ];
    }
    default:
      // free_text (通常テキスト / SSML / 音声ファイル) はプレースホルダを展開しない。
      return [];
  }
}

/**
 * プレビュー条件の遅延の「初期値 (代表値)」を導出する。
 *
 * {@link deriveSampleDelaySeconds} の結果を {@link MAX_INITIAL_SAMPLE_DELAY_SECONDS} (15 分) で
 * 頭打ちにする。最後のパターン行は「60 分以上」のことが多く、そのままだと初期値が 60 分になるため。
 * 行ごとの試聴 (`deriveSampleDelaySeconds` を直接使う側) は丸めない。
 */
function deriveInitialDelaySeconds(pattern: DelayBroadcastPattern | undefined): number {
  return Math.min(deriveSampleDelaySeconds(pattern), MAX_INITIAL_SAMPLE_DELAY_SECONDS);
}

/**
 * 放送コンテンツ 1 件の設定値から「サンプル条件の自動導出値」を組み立てる。
 *
 * ユーザーが何も入力しなくても「その設定で実際に鳴りそうな値」で試聴できるようにする
 * ためのもので、条件パネルで手入力された値は呼び出し側で優先される
 * (`AudioPreviewField` の `touchedKeys` と同じ方針)。
 *
 * 遅延の代表値 (最後のパターン行) は 15 分を上限にする (`deriveInitialDelaySeconds`)。
 */
export function deriveContentSampleOverrides(
  content: BroadcastContent,
): Partial<PreviewSampleContext> {
  switch (content.category) {
    case 'self_train_delay': {
      const props = content.properties as { delay_patterns?: DelayBroadcastPattern[] };
      const patterns = Array.isArray(props.delay_patterns) ? props.delay_patterns : [];
      // 最後の行 (= 最も遅延が大きい行) を代表値にする。
      // 「一番軽い遅れ」より「実際に問題になる遅れ」で聴きたいケースが多い。
      return { delaySeconds: deriveInitialDelaySeconds(patterns[patterns.length - 1]) };
    }
    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      const patterns = Array.isArray(props.delay_patterns) ? props.delay_patterns : [];
      const overrides: Partial<PreviewSampleContext> = {};
      if (patterns.length > 0) {
        overrides.delaySeconds = deriveInitialDelaySeconds(patterns[patterns.length - 1]);
      }
      if (props.route_nickname) overrides.routeName = props.route_nickname;
      else if (props.route_id) overrides.routeName = props.route_id;
      if (props.route_id) overrides.routeId = props.route_id;
      if (props.transfer_stop_id) overrides.fromStation = props.transfer_stop_id;
      if (props.direction_name) overrides.directionName = props.direction_name;
      // part1 は接続線 1 本 / 方面 1 つだが、`lines[]` にも同じ値を載せて
      // サーバ側の突き合わせ規則 (route_id 一致 → 同 index) を統一する (Issue #445)。
      return normalizeSampleContext(overrides);
    }
    case 'connecting_line_delay_by_station': {
      const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
      const overrides: Partial<PreviewSampleContext> = {};
      if (props.transfer_stop_id) overrides.transferStation = props.transfer_stop_id;

      const lines = Array.isArray(props.lines) ? props.lines : [];
      /*
       * 接続線・方面は複数設定され得るため **全件** をプレビュー条件へ展開する
       * (Issue #445。従来は `lines[0]` / `directions[0]` のみで、
       *  2 本目以降の接続線・2 つ目以降の方面が試聴できなかった)。
       */
      if (lines.length > 0) {
        overrides.lines = lines.map((line) => {
          const patterns = Array.isArray(line.delay_patterns) ? line.delay_patterns : [];
          const entry: PreviewSampleLineContext = {
            routeId: line.route_id || undefined,
            routeName: line.route_nickname || line.route_id || undefined,
            directionNames: (Array.isArray(line.directions) ? line.directions : [])
              .map((direction) => direction.direction_name)
              .filter((name): name is string => !!name),
          };
          if (patterns.length > 0) {
            // 接続線ごとに遅延パターンが異なるため、その接続線で実際に鳴る値を持たせる。
            entry.delaySeconds = deriveInitialDelaySeconds(patterns[patterns.length - 1]);
          }
          return entry;
        });
        // 代表値 (フラットキーの delaySeconds) は先頭行から埋める = 既存の挙動を維持。
        const headDelay = overrides.lines[0]?.delaySeconds;
        if (headDelay !== undefined) overrides.delaySeconds = headDelay;
      }
      // 先頭行を代表値としてフラットキーへ写す (既存挙動 / 旧サーバ互換)。
      return normalizeSampleContext(overrides);
    }
    default:
      return {};
  }
}

/**
 * 放送コンテンツ 1 件から「プレビュー条件で指定できる入力枠」を導出する
 * — Issue #445 (レビュー反映: 「現在設定可能な項目に応じて設定できるように」)。
 *
 * ## 何を解決するか
 *
 * 以前は接続線・方面の条件行を任意に増減できたため、**コンテンツに存在しない
 * 接続線 / 方面まで入力できてしまった**。サーバ (`preview_render.py`) は
 * `properties.lines[]` を辿って文面を組み立てるので、設定に無い接続線を
 * プレビュー条件へ足しても音声には反映されず「入力したのに鳴らない」状態になる。
 *
 * そこで **設定内容そのものを入力枠の定義** として返す:
 *
 * - `connecting_line_delay_by_station` (part2): `lines[]` の件数ぶんの枠を作り、
 *   各枠の方面欄数は `lines[j].directions[]` の件数に合わせる
 *   (方面は仕様上 1〜2 件なので、方面欄も最大 2 件までしか出ない)。
 * - `connecting_line_delay` (part1) / `self_train_delay`: 接続線・方面はそれぞれ
 *   1 つしか設定できないため枠は 1 行。方面欄は方面を読み上げるモード
 *   (`transfer_aware` / `section_scheduled`) でのみ 1 件出す。
 * - `free_text`: 線名・方面の概念が無いので枠なし。
 */
export function deriveContentSampleLimits(content: BroadcastContent): PreviewSampleLimits {
  switch (content.category) {
    case 'self_train_delay':
      // 自路線は 1 本、方面の概念は無い (凡例にも {direction_name} が無い)。
      // `kind: 'self'` により遅延欄が「自列車遅延 (分)」として出る (Issue #468)。
      return { lines: [{ label: '自路線', kind: 'self', maxDirections: 0 }] };
    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      const mode: ConnectingDelayJudgementMode = props.delay_judgement_mode ?? 'whole_line';
      return {
        lines: [
          {
            routeId: props.route_id || undefined,
            label: props.route_nickname || props.route_id || '接続線',
            // 接続線の行なので行ごとの「接続線遅延 (分)」欄を出す (Issue #468)。
            kind: 'connecting',
            // whole_line は本番でも方面を読み上げないため方面欄を出さない。
            maxDirections: mode === 'whole_line' ? 0 : 1,
            directionLabels: props.direction_name ? [props.direction_name] : [],
          },
        ],
      };
    }
    case 'connecting_line_delay_by_station': {
      const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
      const lines = Array.isArray(props.lines) ? props.lines : [];
      return {
        lines: lines.map((line, index) => {
          const directions = Array.isArray(line.directions) ? line.directions : [];
          return {
            routeId: line.route_id || undefined,
            label: line.route_nickname || line.route_id || `接続線#${index + 1}`,
            // 接続線ごとに遅延分数を指定できるようにする (Issue #468)。
            kind: 'connecting',
            /*
             * 方面欄は「設定されている方面の件数」ぶんだけ出す。
             *
             * ただし判定モードが `whole_line` (接続線全体の遅延) の接続線は
             * **方面の入力自体を求めない** モードなので方面欄を出さない
             * (part1 の whole_line と同じ扱い)。内部的には方面 1 件を保持するが、
             * 放送文でも方面名は読み上げないため、条件パネルに出すと
             * 「入力したのに鳴らない」状態になってしまう。
             */
            maxDirections: requiresConnectingStationDirections(line.delay_judgement_mode)
              ? directions.length
              : 0,
            directionLabels: directions.map((direction) => direction.direction_name ?? ''),
          };
        }),
      };
    }
    default:
      // free_text (通常テキスト / SSML / 音声ファイル) は線名・方面を持たない。
      return { lines: [] };
  }
}

/**
 * 放送地点まるごとプレビュー (通し試聴) の入力枠を全コンテンツから畳み込む
 * — Issue #445 (レビュー反映)。
 *
 * 通し試聴は 1 つの条件パネルで全コンテンツぶんを指定するため、
 * **`route_id` 単位で枠をマージ** する (`derivePointSampleOverrides` と同じ規則)。
 * 同じ接続線が複数コンテンツに現れる場合、方面欄の数は最大値を採用する
 * (足りないと一部コンテンツの方面を指定できなくなるため)。
 * `route_id` が無い枠は突き合わせできないのでそのまま並べる。
 */
export function derivePointSampleLimits(contents: BroadcastContent[]): PreviewSampleLimits {
  const merged: PreviewSampleLineLimit[] = [];
  for (const content of contents) {
    for (const limit of deriveContentSampleLimits(content).lines) {
      const existing = limit.routeId
        ? merged.find((entry) => entry.routeId === limit.routeId)
        : undefined;
      if (!existing) {
        merged.push({ ...limit, directionLabels: [...(limit.directionLabels ?? [])] });
        continue;
      }
      // 方面欄が足りないと一部コンテンツの方面を指定できないので最大値を採る。
      existing.maxDirections = Math.max(existing.maxDirections, limit.maxDirections);
      const labels = existing.directionLabels ?? [];
      for (const [index, label] of (limit.directionLabels ?? []).entries()) {
        if (!labels[index] && label) labels[index] = label;
      }
      existing.directionLabels = labels;
      existing.label ??= limit.label;
    }
  }
  return { lines: merged };
}

/**
 * 放送地点まるごとプレビュー用の「サンプル条件の自動導出値」を全コンテンツから畳み込む。
 *
 * **先頭のコンテンツを優先** する (`??=` 相当)。同じ地点の複数コンテンツで
 * 乗換駅・方面が食い違う場合、放送順で先に鳴るものを代表値にする方が直感的なため。
 *
 * 条件パネルの「未編集のキーだけ自動導出値へ追従する」判定 (`usePreviewSample`) に
 * そのまま渡せるよう、**凡例由来の既定値は含めない** (コンテンツ設定から
 * 実際に導出できた値のみを返す)。
 *
 * ただし `lines` (複数条件 / Issue #445) だけは例外で、**route_id 単位でマージ** する。
 * 先頭コンテンツ優先にすると 2 件目以降のコンテンツの接続線が通し試聴で鳴らなくなり、
 * 「複数の接続線・方面をまとめて確認する」という本来の目的を果たせないため。
 */
export function derivePointSampleOverrides(
  contents: BroadcastContent[],
): Partial<PreviewSampleContext> {
  const derived: Partial<PreviewSampleContext> = {};
  const mergedLines: PreviewSampleLineContext[] = [];
  for (const content of contents) {
    for (const [key, value] of Object.entries(deriveContentSampleOverrides(content))) {
      if (value === undefined) continue;
      if (key === 'lines') {
        mergeSampleLines(mergedLines, value as PreviewSampleLineContext[]);
        continue;
      }
      if ((derived as Record<string, unknown>)[key] === undefined) {
        (derived as Record<string, unknown>)[key] = value;
      }
    }
  }
  if (mergedLines.length > 0) derived.lines = mergedLines;
  return derived;
}

/**
 * 接続線条件を route_id 単位でマージする (`derivePointSampleOverrides` の補助)。
 *
 * 同じ route_id が既にあれば **方面名を追記** (重複は除く) し、
 * 未登録の route_id は末尾へ追加する。route_id 未設定の行は突き合わせ不能なので
 * そのまま末尾へ追加する (行数が増えるだけで文面は壊れない)。
 */
function mergeSampleLines(
  target: PreviewSampleLineContext[],
  additions: PreviewSampleLineContext[],
): void {
  for (const line of additions) {
    if (!line) continue;
    const existing = line.routeId
      ? target.find((entry) => entry.routeId === line.routeId)
      : undefined;
    if (!existing) {
      target.push({ ...line, directionNames: [...(line.directionNames ?? [])] });
      continue;
    }
    const names = existing.directionNames ?? [];
    for (const name of line.directionNames ?? []) {
      if (!names.includes(name)) names.push(name);
    }
    existing.directionNames = names;
    existing.routeName ??= line.routeName;
    existing.delaySeconds ??= line.delaySeconds;
  }
}

/**
 * 放送地点まるごとプレビュー用のサンプル条件を、全コンテンツから組み立てる。
 *
 * - 凡例は全コンテンツぶんを結合して `buildSampleContext()` に渡す
 *   (= どのコンテンツで使われているトークンでも既定値が入る)。
 * - 自動導出値 (`derivePointSampleOverrides`) は凡例の既定値より優先される。
 * - `overrides` (ユーザーが条件パネルで指定した値) は常に最優先。
 */
export function buildPointSampleContext(
  contents: BroadcastContent[],
  overrides: Partial<PreviewSampleContext> = {},
): PreviewSampleContext {
  const items = contents.flatMap((content) => resolveContentPlaceholders(content));
  return buildSampleContext(items, {
    ...derivePointSampleOverrides(contents),
    ...stripUndefined(overrides),
  });
}

/**
 * 放送地点まるごとプレビューの条件パネルに出す入力欄を、全コンテンツから判定する。
 *
 * コンテンツが 1 件も無い / free_text だけの地点では全項目 false になり、
 * 条件パネル自体を隠せる (使えない条件を見せない)。
 */
export function resolvePointSampleFields(
  contents: BroadcastContent[],
): PreviewSampleFieldVisibility {
  return mergeSampleFields(
    contents.map((content) => resolveSampleFields(resolveContentPlaceholders(content))),
  );
}
